// Instagram Channel Service for frontend-react
// Uses Conversations API for Instagram channel connection

import { CONVERSATIONS_API_URL, META_INTEGRATION_ENABLED, getConversationsAuthToken } from '@/config/env'

export interface InstagramConnectionStatus {
  connected: boolean
  /** Instagram Business Account ID — the API returns it as `account_id`. */
  account_id?: string
  page_id?: string
  metadata?: {
    instagram_account_id?: string
    page_id?: string
    page_name?: string
    instagram_username?: string
    instagram_name?: string
    [key: string]: unknown
  }
}

export interface InstagramConnectData {
  instagram_account_id: string
  page_access_token: string
  page_id: string
  /** Lets the backend derive a long-lived Page token instead of storing the short-lived one. */
  user_access_token?: string
  redirect_uri?: string
}

export class InstagramServiceError extends Error {
  public status?: number
  
  constructor(message: string, status?: number) {
    super(message)
    this.status = status
    this.name = 'InstagramServiceError'
  }
}

export interface InstagramMedia {
  id: string
  caption?: string
  media_type?: string
  media_url?: string
  thumbnail_url?: string
  permalink?: string
  timestamp?: string
  comments_count?: number
  like_count?: number
  insights?: Record<string, number | null>
}

export interface InstagramComment {
  id: string
  text: string
  username?: string
  timestamp?: string
  hidden?: boolean
  like_count?: number
  replies?: { data: { id: string; text: string; username?: string; timestamp?: string }[] }
}

export interface InstagramCommentActivity {
  comment_id: string
  media_id: string
  username: string
  text: string
  received_at: string
  dm_sent: boolean
  public_reply_sent: boolean
  skipped?: boolean
  error: string
}

export interface InstagramCommentAutomation {
  enabled: boolean
  keyword: string
  dm_message: string
  public_reply: string
  activity?: InstagramCommentActivity[]
  /** Facebook only: whether the Page could be subscribed to new comments. */
  feed_subscribed?: boolean
  feed_subscription_error?: string
}

/** Channels whose posts and comments the backend returns in the same shape. */
export type CommentChannel = 'instagram' | 'facebook'

export interface InstagramInsights {
  profile: {
    username?: string
    name?: string
    profile_picture_url?: string
    followers_count?: number
    follows_count?: number
    media_count?: number
  }
  period_days: number
  metrics: Record<string, number | null>
  errors: Record<string, string>
  media: InstagramMedia[]
}

class InstagramServiceClient {
  private baseUrl: string
  private channelPath: string

  /**
   * `channelPath` lets the Facebook Page reuse the comment methods: its
   * endpoints mirror Instagram's under `/auth/messenger/`.
   */
  constructor(channelPath = '/api/v1/auth/instagram') {
    this.baseUrl = CONVERSATIONS_API_URL || ''
    this.channelPath = channelPath
  }

  private getAuthToken(): string {
    return getConversationsAuthToken()
  }

  protected async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!META_INTEGRATION_ENABLED) {
      throw new InstagramServiceError('Meta integration is disabled in this environment', 503)
    }

    const url = `${this.baseUrl}${endpoint}`
    
    const config: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options
    }

    // Add authentication token
    const token = this.getAuthToken()
    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Token ${token}`,  // Use Token auth for user-specific endpoints
      }
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new InstagramServiceError(
          `HTTP ${response.status}: ${errorText || response.statusText}`,
          response.status
        )
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json()
        return data
      } else {
        const text = await response.text()
        return text as T
      }
    } catch (error) {
      if (error instanceof InstagramServiceError) {
        throw error
      }
      throw new InstagramServiceError(`Network error: ${error}`, 0)
    }
  }

  /**
   * Connect Instagram channel
   */
  async connect(data: InstagramConnectData): Promise<InstagramConnectionStatus> {
    return this.request<InstagramConnectionStatus>('/api/v1/auth/instagram/connect/', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Connect through the Instagram API with Instagram Login.
   *
   * Uses the `instagram_business_*` permissions, which are approved separately
   * from the Page-based flow's `instagram_manage_messages`.
   */
  async connectWithInstagramLogin(code: string, redirectUri: string): Promise<InstagramConnectionStatus> {
    return this.request<InstagramConnectionStatus>('/api/v1/auth/instagram/connect/', {
      method: 'POST',
      body: JSON.stringify({
        auth_flow: 'instagram_login',
        code,
        redirect_uri: redirectUri,
      }),
    })
  }

  /**
   * Disconnect Instagram channel
   */
  async disconnect(): Promise<void> {
    return this.request<void>('/api/v1/auth/instagram/disconnect/', {
      method: 'POST'
    })
  }

  /**
   * Get Instagram connection status
   */
  async getStatus(): Promise<InstagramConnectionStatus> {
    const response = await this.request<InstagramConnectionStatus>('/api/v1/auth/instagram/status/')
    return response
  }

  async listMedia(): Promise<InstagramMedia[]> {
    const data = await this.request<{ media: InstagramMedia[] }>(`${this.channelPath}/media/`)
    return data.media
  }

  async listComments(mediaId: string): Promise<InstagramComment[]> {
    const data = await this.request<{ comments: InstagramComment[] }>(
      `${this.channelPath}/media/${encodeURIComponent(mediaId)}/comments/`
    )
    return data.comments
  }

  async replyToComment(commentId: string, message: string): Promise<void> {
    await this.request(`${this.channelPath}/comments/${encodeURIComponent(commentId)}/reply/`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    })
  }

  /** DM to the person who wrote the comment (Meta's «private reply»). */
  async sendPrivateReply(commentId: string, message: string): Promise<void> {
    await this.request(`${this.channelPath}/comments/${encodeURIComponent(commentId)}/private-reply/`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    })
  }

  async setCommentHidden(commentId: string, hidden: boolean): Promise<void> {
    await this.request(`${this.channelPath}/comments/${encodeURIComponent(commentId)}/hide/`, {
      method: 'POST',
      body: JSON.stringify({ hidden }),
    })
  }

  async getCommentAutomation(): Promise<InstagramCommentAutomation> {
    return this.request<InstagramCommentAutomation>(`${this.channelPath}/comment-automation/`)
  }

  async saveCommentAutomation(rule: InstagramCommentAutomation): Promise<InstagramCommentAutomation> {
    return this.request<InstagramCommentAutomation>(`${this.channelPath}/comment-automation/`, {
      method: 'PUT',
      body: JSON.stringify(rule),
    })
  }

  async getInsights(): Promise<InstagramInsights> {
    return this.request<InstagramInsights>('/api/v1/auth/instagram/insights/')
  }
}

// Export singleton instance
export const instagramService = new InstagramServiceClient()

export interface UtilityTemplate {
  id: string
  name: string
  language: string
  status: string
  body: string
  param_count: number
}

export interface UtilityTemplateInput {
  name: string
  language: string
  body: string
  examples: string[]
}

export interface MarketingSubscriber {
  token: string
  psid: string
  status?: string
  topic?: string
  created_at?: string
}

export interface MarketingSendResult {
  campaign_id: string
  subscribers: number
  sent: number
  errors: string[]
}

export interface MetaBusiness {
  id: string
  name: string
}

export interface ProductCatalog {
  id: string
  name: string
  product_count: number
}

export interface CatalogProduct {
  id: string
  retailer_id: string
  name: string
  description?: string
  /** Formatted by Meta, e.g. "COP250,000.00". */
  price?: string
  currency?: string
  image_url?: string
  url?: string
}

export interface ManagedMetaApp {
  id: string
  name: string
  link: string
  category: string
  /** What the user may do on the app, e.g. MANAGE, DEVELOP. */
  tasks: string[]
  solutions: { id: string; name: string; status: string; owner_app: string }[]
}

export interface CatalogProductInput {
  name: string
  description: string
  price: string
  currency: string
  image_url: string
  url: string
}

/**
 * The connected Facebook Page: its comments (same methods as Instagram), the
 * Messenger messages allowed outside the 24-hour window, and the product
 * catalogs of the business behind it.
 */
class FacebookPageServiceClient extends InstagramServiceClient {
  async listUtilityTemplates(): Promise<UtilityTemplate[]> {
    const data = await this.request<{ templates: UtilityTemplate[] }>('/api/v1/auth/messenger/utility-templates/')
    return data.templates
  }

  async createUtilityTemplate(template: UtilityTemplateInput): Promise<UtilityTemplate> {
    return this.request<UtilityTemplate>('/api/v1/auth/messenger/utility-templates/', {
      method: 'POST',
      body: JSON.stringify(template),
    })
  }

  async sendUtilityMessage(conversationId: string, templateName: string, language: string, params: string[]): Promise<void> {
    await this.request('/api/v1/auth/messenger/utility-messages/', {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId, template_name: templateName, language, params }),
    })
  }

  async sendMarketingOptin(conversationId: string, title: string, timezone: string): Promise<void> {
    await this.request('/api/v1/auth/messenger/marketing/optin/', {
      method: 'POST',
      body: JSON.stringify({ conversation_id: conversationId, title, timezone }),
    })
  }

  async listMarketingSubscribers(): Promise<MarketingSubscriber[]> {
    const data = await this.request<{ subscribers: MarketingSubscriber[] }>('/api/v1/auth/messenger/marketing/subscribers/')
    return data.subscribers
  }

  async listManagedApps(): Promise<ManagedMetaApp[]> {
    const data = await this.request<{ apps: ManagedMetaApp[] }>('/api/v1/auth/meta/apps/')
    return data.apps
  }

  async listBusinesses(): Promise<MetaBusiness[]> {
    const data = await this.request<{ businesses: MetaBusiness[] }>('/api/v1/auth/meta/businesses/')
    return data.businesses
  }

  async listCatalogs(businessId: string): Promise<ProductCatalog[]> {
    const data = await this.request<{ catalogs: ProductCatalog[] }>(
      `/api/v1/auth/meta/catalogs/?business_id=${encodeURIComponent(businessId)}`
    )
    return data.catalogs
  }

  async createCatalog(businessId: string, name: string): Promise<ProductCatalog> {
    return this.request<ProductCatalog>('/api/v1/auth/meta/catalogs/', {
      method: 'POST',
      body: JSON.stringify({ business_id: businessId, name }),
    })
  }

  async listCatalogProducts(catalogId: string): Promise<CatalogProduct[]> {
    const data = await this.request<{ products: CatalogProduct[] }>(
      `/api/v1/auth/meta/catalogs/${encodeURIComponent(catalogId)}/products/`
    )
    return data.products
  }

  async createCatalogProduct(catalogId: string, product: CatalogProductInput): Promise<CatalogProduct> {
    return this.request<CatalogProduct>(`/api/v1/auth/meta/catalogs/${encodeURIComponent(catalogId)}/products/`, {
      method: 'POST',
      body: JSON.stringify(product),
    })
  }

  async deleteCatalogProduct(productId: string): Promise<void> {
    await this.request(`/api/v1/auth/meta/products/${encodeURIComponent(productId)}/`, { method: 'DELETE' })
  }

  async sendMarketingMessage(adAccountId: string, text: string, lifetimeBudget: number): Promise<MarketingSendResult> {
    return this.request<MarketingSendResult>('/api/v1/auth/messenger/marketing/send/', {
      method: 'POST',
      body: JSON.stringify({ ad_account_id: adAccountId, text, lifetime_budget: lifetimeBudget }),
    })
  }
}

export const facebookPageService = new FacebookPageServiceClient('/api/v1/auth/messenger')

export const commentServices: Record<CommentChannel, InstagramServiceClient> = {
  instagram: instagramService,
  facebook: facebookPageService,
}

